import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { z } from 'zod/v3';
import { COLUMN_ACTION_GROUP } from '../../constants';
import { GraphModel } from '@cotera/client/v0/graph/graph.model';
import { TaxonomyColumnModel } from '../taxonomy-column.model';
import { Assert, assertExists } from '@cotera/utilities';
import { DG } from '@cotera/sdk/core';
import { relColSchema } from '@cotera/api';
import {
  ModalPageView,
  toast,
  Loading,
  CollapsibleError,
} from '@cotera/client/app/components';
import { err, ok } from 'neverthrow';
import { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { useState } from 'react';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Label } from '@cotera/client/v0/components/ui/label';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Params } from '@sentry/react/types/types';
import React from 'react';
import { findColumnNameConflict } from '../../utils/column-name-validation';

export class SaveTaxonomyColumnAction extends BaseAction<
  /* ActionPayload= */ {},
  /* Errors= */ { t: string; message: string }[]
> {
  override title = 'Save Column';
  override inputSchema = z.object({});
  shortcut = ['ctr', 'shift', 's'];
  group = COLUMN_ACTION_GROUP;
  icon = 'check';

  constructor(
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      close: () => void;
    }
  ) {
    super();
  }

  /**
   * GraphModel & ExpressionColumnModel must be in scope. The exprModel's dataset must be ok.
   */
  override applicable(context: ApplicableContext): boolean {
    if (!context.isInScope(GraphModel)) {
      return false;
    }
    if (!context.hasTargetedModelOfType(TaxonomyColumnModel)) {
      return false;
    }

    const taxonomyColModel = assertExists(
      context.targetedModelOfType(TaxonomyColumnModel)
    );

    const datasetRel = taxonomyColModel.datasetRel.snapshot();
    if (datasetRel === null) {
      return false;
    }

    return datasetRel.isOk();
  }

  override async execute(
    _payload: {},
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message: string }[]>> {
    const taxonomyModel = context.targetedModelOfType(TaxonomyColumnModel);
    const graphModel = context.getInScopeModelOfType(GraphModel);

    // by `applicable()`
    Assert.assert(taxonomyModel !== null);
    Assert.assert(graphModel !== null);

    const colName = taxonomyModel.formState.snapshot().name;
    const relName = taxonomyModel.datasetName;

    // Check for column name conflicts in current dataset and parent lineage
    const conflictDataset = findColumnNameConflict(
      graphModel.graph.snapshot(),
      relName,
      colName,
      !taxonomyModel.isNew // Exclude current column if it's a rename (not new)
    );

    if (conflictDataset !== null) {
      return err([
        {
          t: 'error',
          message: `A column with the name "${colName}" already exists in dataset "${conflictDataset}"`,
        },
      ]);
    }

    const opParams: {
      name: string;
      relName: string;
      config: z.infer<typeof relColSchema>;
    } = {
      name: colName,
      relName,
      config: {
        t: 'eraql',
        source: taxonomyModel.eraQL.snapshot() || '',
      },
    };

    const operation = taxonomyModel.isNew
      ? DG.Operations.createColumn(opParams)
      : DG.Operations.updateColumn(opParams);

    const tempCommit: DG.Commit = {
      actorId: 'system',
      timestamp: new Date(),
      operations: [operation],
      message: null,
    };

    // graphModel.compiler is already loaded with staged operations
    const datasetCompilationResult = graphModel.compiler
      .snapshot()
      .commit(tempCommit)
      .compile()
      .map((x) => x.getColumn({ name: colName, rel: relName })?.config);

    Assert.assert(datasetCompilationResult !== undefined); // dataset exists at this point

    if (datasetCompilationResult.isErr()) {
      const errorMessage = datasetCompilationResult.error.prettyPrint();
      toast.error('Failed to compile column', {
        description: <CollapsibleError error={errorMessage} />,
      });
      return err([{ t: 'error', message: errorMessage }]);
    }
    const stagingResult = await graphModel.attemptToStageOperations([
      operation,
    ]);

    if (stagingResult.isErr()) {
      const errorMessage = stagingResult.error.prettyPrint();
      toast.error('Failed to stage operations', {
        description: <CollapsibleError error={errorMessage} />,
      });
      return err([{ t: 'error', message: errorMessage }]);
    }

    toast.success('Changes saved');

    return ok({});
  }

  shouldAsk(context: ApplicableContext): boolean {
    const taxonomyModel = context.targetedModelOfType(TaxonomyColumnModel);
    Assert.assert(taxonomyModel !== null); // by `applicable`
    return taxonomyModel.isNew;
  }

  ask(_: {}, context: ExecuteContext): AskResponse<{}> {
    const taxonomyModel = context.targetedModelOfType(TaxonomyColumnModel);
    Assert.assert(taxonomyModel !== null); // by `applicable`

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <AskView
                onRespond={async (params) => {
                  await onRespond(params);
                  this.modalManager.close();
                }}
                taxonomyModel={taxonomyModel}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

export const AskView: React.FC<{
  onRespond: (params: Params) => Promise<void>;
  taxonomyModel: TaxonomyColumnModel;
}> = ({ onRespond, taxonomyModel }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const graph = taxonomyModel.inputs.graph.snapshot();
  const conflictDataset = findColumnNameConflict(
    graph,
    taxonomyModel.datasetName,
    name,
    false
  );

  const validName = conflictDataset === null && name.trim().length > 0;

  return (
    <ModalPageView
      title={`Save ${taxonomyModel.currentNameInGraph.snapshot()}`}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="taxonomy-column-name" className="text-sm">
            Column Name
          </Label>
          {conflictDataset !== null && (
            <span className="text-xs text-destructive">
              A column with this name already exists in dataset "
              {conflictDataset}"
            </span>
          )}
          <Input
            id="taxonomy-column-name"
            placeholder="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="w-full flex justify-end">
          <Button
            type="submit"
            variant="default"
            disabled={!validName || loading}
            icon="save"
            className="w-fit self-end"
            onClick={async () => {
              setLoading(true);
              React.startTransition(() => {
                taxonomyModel.formState.set({
                  ...taxonomyModel.formState.snapshot(),
                  name,
                });
              });
              await onRespond({ to: name });
              setLoading(false);
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Save'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
